"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findRateOrParentRate = exports.buildOrderTaxMarkersByRateId = exports.sortOrderTaxTotalsCanonically = exports.calculateBidTaxTotals = exports.calculateBidsTaxAmount = exports.calculateOrderTaxAmount = exports.calculateOrderTaxTotals = exports.buildTaxableSaleUnits = exports.calculateTaxesForSaleUnit = exports.calculateTaxAmount = exports.TAX_PPM_DENOMINATOR = void 0;
const ticketing_1 = require("../enums/ticketing");
const discount_calculator_1 = require("../discount/discount-calculator");
exports.TAX_PPM_DENOMINATOR = 1_000_000;
const calculateTaxAmount = (baseCents, ratePpm) => {
    return Math.floor((baseCents * ratePpm + exports.TAX_PPM_DENOMINATOR / 2) / exports.TAX_PPM_DENOMINATOR);
};
exports.calculateTaxAmount = calculateTaxAmount;
const calculateTaxesForSaleUnit = (unitAmount, unitDiscountAmount, taxes) => {
    const taxableBase = Math.max(unitAmount - unitDiscountAmount, 0);
    return taxes
        .map((tax) => ({ taxId: tax.id, ratePpm: tax.ratePpm, amount: (0, exports.calculateTaxAmount)(taxableBase, tax.ratePpm) }))
        .filter((line) => line.amount > 0);
};
exports.calculateTaxesForSaleUnit = calculateTaxesForSaleUnit;
const buildTaxableSaleUnits = (params) => {
    const { tickets, rates, discount, discountRateMappings = [] } = params;
    const purchasedTickets = tickets.filter((ticket) => (ticket.purchasedNumber || 0) > 0);
    const fullOrderUnitDiscounts = discount && discount.scope === ticketing_1.DiscountScope.FullOrder
        ? (0, discount_calculator_1.allocateFullOrderDiscount)(discount, listFullOrderUnitAmounts(purchasedTickets, rates))
        : [];
    const units = [];
    let fullOrderUnitIndex = 0;
    for (const ticket of purchasedTickets) {
        const rate = (0, exports.findRateOrParentRate)(ticket.rateId, rates);
        if (!rate) {
            continue;
        }
        const unitAmount = rate.isPayWhatYouCan ? ticket.pwycAmount || 0 : rate.amount;
        for (let unit = 0; unit < ticket.purchasedNumber; unit++) {
            const unitDiscountAmount = (0, discount_calculator_1.calculateTicketLineDiscount)({
                discount,
                discountRateMappings,
                rate,
                ticketRateId: ticket.rateId,
                unitAmount,
                fullOrderUnitDiscountAmount: fullOrderUnitDiscounts[fullOrderUnitIndex] ?? 0,
            });
            if (discount?.scope === ticketing_1.DiscountScope.FullOrder && !rate.isPayWhatYouCan) {
                fullOrderUnitIndex++;
            }
            units.push({
                rateId: ticket.rateId,
                resolvedRateId: rate.id,
                unitAmount,
                unitDiscountAmount,
                taxes: (0, exports.calculateTaxesForSaleUnit)(unitAmount, unitDiscountAmount, rate.taxes ?? []),
            });
        }
    }
    return units;
};
exports.buildTaxableSaleUnits = buildTaxableSaleUnits;
const listFullOrderUnitAmounts = (purchasedTickets, rates) => {
    const unitAmounts = [];
    for (const ticket of purchasedTickets) {
        const rate = (0, exports.findRateOrParentRate)(ticket.rateId, rates);
        if (!rate || rate.isPayWhatYouCan) {
            continue;
        }
        for (let unit = 0; unit < ticket.purchasedNumber; unit++) {
            unitAmounts.push(rate.amount);
        }
    }
    return unitAmounts;
};
const calculateOrderTaxTotals = (params) => {
    const units = (0, exports.buildTaxableSaleUnits)(params);
    const taxesById = new Map();
    for (const rate of params.rates) {
        for (const tax of rate.taxes ?? []) {
            if (!taxesById.has(tax.id)) {
                taxesById.set(tax.id, { name: tax.name, ratePpm: tax.ratePpm });
            }
        }
    }
    const totals = [];
    for (const [taxId, tax] of taxesById) {
        const amount = units.reduce((sum, unit) => sum + (unit.taxes.find((line) => line.taxId === taxId)?.amount ?? 0), 0);
        if (amount > 0) {
            totals.push({ taxId, name: tax.name, ratePpm: tax.ratePpm, amount });
        }
    }
    return totals;
};
exports.calculateOrderTaxTotals = calculateOrderTaxTotals;
const calculateOrderTaxAmount = (params) => {
    return (0, exports.calculateOrderTaxTotals)(params).reduce((sum, total) => sum + total.amount, 0);
};
exports.calculateOrderTaxAmount = calculateOrderTaxAmount;
const calculateBidsTaxAmount = (bids, rates) => bids.reduce((sum, bid) => {
    const taxes = rates.find((rate) => rate.id === bid.rateId)?.taxes ?? [];
    return sum + (0, exports.calculateTaxesForSaleUnit)(bid.amount, 0, taxes).reduce((lineSum, line) => lineSum + line.amount, 0);
}, 0);
exports.calculateBidsTaxAmount = calculateBidsTaxAmount;
const calculateBidTaxTotals = (bids, rates) => {
    const totalsByTaxId = new Map();
    for (const rate of rates) {
        for (const tax of rate.taxes ?? []) {
            if (!totalsByTaxId.has(tax.id)) {
                totalsByTaxId.set(tax.id, { taxId: tax.id, name: tax.name, ratePpm: tax.ratePpm, amount: 0 });
            }
        }
    }
    for (const bid of bids) {
        const taxes = rates.find((rate) => rate.id === bid.rateId)?.taxes ?? [];
        for (const line of (0, exports.calculateTaxesForSaleUnit)(bid.amount, 0, taxes)) {
            const total = totalsByTaxId.get(line.taxId);
            if (total) {
                total.amount += line.amount;
            }
        }
    }
    return [...totalsByTaxId.values()].filter((total) => total.amount > 0);
};
exports.calculateBidTaxTotals = calculateBidTaxTotals;
const sortOrderTaxTotalsCanonically = (totals, canonicalTaxIds) => {
    const indexByTaxId = new Map(canonicalTaxIds.map((taxId, index) => [taxId, index]));
    return [...totals].sort((a, b) => (indexByTaxId.get(a.taxId) ?? 0) - (indexByTaxId.get(b.taxId) ?? 0));
};
exports.sortOrderTaxTotalsCanonically = sortOrderTaxTotalsCanonically;
const buildOrderTaxMarkersByRateId = (totals, rates) => {
    const markersByTaxId = new Map(totals.map((total, index) => [total.taxId, totals.length === 1 ? "T" : `T${index + 1}`]));
    const markersByRateId = {};
    for (const rate of rates) {
        const rateTaxIds = new Set((rate.taxes ?? []).map((tax) => tax.id));
        const markers = totals
            .filter((total) => rateTaxIds.has(total.taxId))
            .map((total) => markersByTaxId.get(total.taxId) ?? "");
        markersByRateId[rate.id] = { taxMarkers: markers.join(", "), hasMultipleTaxMarkers: markers.length > 1 };
    }
    return markersByRateId;
};
exports.buildOrderTaxMarkersByRateId = buildOrderTaxMarkersByRateId;
const findRateOrParentRate = (rateId, rates) => {
    const direct = rates.find((rate) => rate.id === rateId);
    if (direct) {
        return direct;
    }
    return rates.find((rate) => rate.childRateIds?.includes(rateId));
};
exports.findRateOrParentRate = findRateOrParentRate;
